import type { IconWeight } from "@phosphor-icons/react/dist/lib/types";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/Clock";
import { TruckIcon } from "@phosphor-icons/react/Truck";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { Hours } from "@freightsimple/generated-dashboard-openapi-client/models/Hours";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { describeDateRelative } from "@/Helpers/describeDateRelative";
import {
  describeDateRelativeWithTime,
  describeDateRelativeWithYearWithTime,
} from "@/Helpers/describeDateRelativeWithTime";
import { describeHours } from "@/Helpers/describeHours";
import {
  timeLabel2ForShipmentState,
  timeLabelForShipmentState,
  timeLabelTooltipForShipmentState,
} from "@/Helpers/timeLabelForShipmentState";
import moment from "moment";
import type { ComponentType } from "react";
import { shipmentFormatExpectedDeliveryHours } from "@/Shared/Shipment/Helpers/shipmentFormatExpectedDeliveryHours";
import {
  makeTimeDescription,
  TopRight_,
} from "@/Shared/Shipment/Bars/Internal/shipmentTopBarParts";

interface StatusIcon {
  icon: ComponentType<{ className?: string; weight?: IconWeight }>;
  color: string;
}

// The status glyph and its colour. The colour tokens are the exact theme
// values the raw hexes named — gold #facc0b, blue #4c62ea, dark-green #108922,
// very-light-gray #ededed, mid-gray #aaa.
function statusIconForShipmentState(shipmentState: ShipmentState): StatusIcon {
  switch (shipmentState) {
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
      // Don't believe we use this
      return { icon: ClockIcon, color: "text-very-light-gray" };
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
      return { icon: ClockIcon, color: "text-gold" };
    case ShipmentState.OnHold:
      return { icon: ClockIcon, color: "text-mid-gray" };
    case ShipmentState.InTransit:
      return { icon: TruckIcon, color: "text-blue-text" };
    case ShipmentState.Delivered:
      return { icon: CheckCircleIcon, color: "text-dark-green" };
    case ShipmentState.Cancelled:
    case ShipmentState.Lost:
      return { icon: XCircleIcon, color: "text-foreground" };
  }
}

interface EndpointProps {
  businessName: string | undefined;
  city: string;
  cityTestId: string;
}

// One end of the lane: the business on top, its city underneath. Per the text
// colour rubric the business name is a title, so it's crisp `default`, and the
// city is an address — real information, so `secondary`. Both used to sit a
// step dimmer than that, which is what made this bar read as washed out.
function Endpoint_(props: EndpointProps) {
  return (
    <Stack gap={0} align="start">
      <T size="lg" weight="medium">
        {props.businessName}
      </T>
      <T size="sm" weight="normal" color="secondary" testId={props.cityTestId}>
        {props.city}
      </T>
    </Stack>
  );
}

interface TopBarProps {
  shipmentState: ShipmentState;
  pickupLocation: AddLocationLocationType | Location;
  deliveryLocation: AddLocationLocationType | Location;
  pickupDate: string;
  pickupHours: Hours | undefined;
  appointmentDate: string | undefined;
  expectedDeliveryDate: string | undefined;
  expectedDeliveryHours: string | undefined;
  actualDeliveryDate: string | undefined;
  actualDeliveryTime: string | undefined;
  predictedLatestDeliveryDate: string | undefined;
  proNumber: string;
  referenceNumber?: string | undefined;
  preferRenderReferenceNumber?: boolean | undefined;
  billOfLadingNumber: string | undefined;
  deliveryAppointment: boolean;
}

function ShipmentTopBar(props: TopBarProps) {
  const {
    shipmentState,
    pickupLocation,
    deliveryLocation,
    pickupDate,
    pickupHours,
    appointmentDate,
    expectedDeliveryDate,
    expectedDeliveryHours,
    predictedLatestDeliveryDate,
    billOfLadingNumber,
    actualDeliveryDate,
    actualDeliveryTime,
    deliveryAppointment,
  } = props;

  const pickupCity = `${pickupLocation.address.city}, ${pickupLocation.address.stateOrProvinceCode}`;
  const deliveryCity = `${deliveryLocation.address.city}, ${deliveryLocation.address.stateOrProvinceCode}`;

  const pickupBusinessName = pickupLocation.businessName;
  const deliveryBusinessName = deliveryLocation.businessName;

  const pickupDateDescription = describeDateRelativeWithTime(
    pickupDate,
    pickupHours ? describeHours(pickupHours) : undefined,
    // If the pickup was missed, we still want to show eg 'Yesterday'
    // as it will make it clearer to the user what happened
    false,
    true,
  );

  const formattedExpectedDeliveryHours = shipmentFormatExpectedDeliveryHours(
    expectedDeliveryHours,
  );

  const expectedDeliveryDateDescription = describeDateRelativeWithTime(
    expectedDeliveryDate,
    deliveryAppointment ? "by appointment" : formattedExpectedDeliveryHours,
    true,
    true, // compact
  );

  const appointmentDateDescription = appointmentDate
    ? describeDateRelative(appointmentDate, true)
    : undefined;

  const actualDeliveryTimeDescription = actualDeliveryTime
    ? moment(actualDeliveryTime, "HH:mm").format("h:mma")
    : undefined;

  const actualDeliveryDateDescription = describeDateRelativeWithYearWithTime(
    actualDeliveryDate,
    actualDeliveryTimeDescription,
    false,
  );

  const timeLabel = timeLabelForShipmentState(shipmentState, appointmentDate);
  const timeLabel2 = timeLabel2ForShipmentState(shipmentState, appointmentDate);

  const timeLabelTooltip = timeLabelTooltipForShipmentState(shipmentState);

  const timeDescription = makeTimeDescription(
    shipmentState,
    pickupDateDescription,
    expectedDeliveryDateDescription,
    appointmentDateDescription,
    actualDeliveryDateDescription,
  );

  // Show the latest delivery only if the current earliest delivery is before the latest (otherwise it looks bad
  // For BookingConfirmed, show earliest delivery date
  // For InTransit, show latest delivery date if available
  const timeDescription2 =
    shipmentState === ShipmentState.BookingConfirmed &&
    expectedDeliveryDateDescription !== undefined
      ? expectedDeliveryDateDescription
      : shipmentState === ShipmentState.InTransit &&
          predictedLatestDeliveryDate !== undefined &&
          expectedDeliveryDate !== undefined &&
          appointmentDate === undefined &&
          moment(predictedLatestDeliveryDate).isAfter(
            moment(expectedDeliveryDate),
          )
        ? describeDateRelative(predictedLatestDeliveryDate, true)
        : undefined;

  function decideReferenceNumber() {
    if (props.preferRenderReferenceNumber === true) {
      if (props.referenceNumber) {
        return {
          referenceNumber: props.referenceNumber,
          referenceNumberLabel: "Reference",
        };
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  const referenceNumberInfo = decideReferenceNumber();

  const statusIcon = statusIconForShipmentState(shipmentState);

  return (
    <Row justify="between" align="center" gap={6}>
      {/* The lane. `shrinkable` lets it give up width so the right-hand block
          — whose values can't wrap — always renders in full. */}
      <Row gap={4} align="center" shrinkable>
        <Icon
          icon={statusIcon.icon}
          size="2xl"
          weight="fill"
          color={statusIcon.color}
        />
        <Endpoint_
          businessName={pickupBusinessName}
          city={pickupCity}
          cityTestId="pickup-city"
        />
        <Icon
          icon={ArrowRightIcon}
          size="sm"
          weight="fill"
          color="text-foreground"
        />
        <Endpoint_
          businessName={deliveryBusinessName}
          city={deliveryCity}
          cityTestId="delivery-city"
        />
      </Row>

      <TopRight_
        timeLabel={timeLabel}
        timeDescription={timeDescription}
        timeLabelTooltip={timeLabelTooltip}
        timeLabel2={timeLabel2}
        timeDescription2={timeDescription2}
        shipmentState={shipmentState}
        referenceNumber={referenceNumberInfo?.referenceNumber}
        referenceNumberLabel={referenceNumberInfo?.referenceNumberLabel}
        billOfLadingNumber={billOfLadingNumber}
      />
    </Row>
  );
}
export default ShipmentTopBar;
